// ────────────────────────────────────────────────────────────────────────────
// Forward-migration for saved layouts.
//
// `schemaVersion` exists so a shape change does not orphan layouts an operator
// has already designed. This is where that promise is kept — and it applies
// equally once layouts live in `OrganisationDocumentTemplate.LayoutJson`, so
// the same function should run on read there.
//
// v1 → v2: the block describing the company issuing the document was called
// "seller". It is the organisation providing the service, so the band kind and
// its tokens were renamed. Layouts saved as v1 carry `kind: 'seller'` (which no
// renderer branch would match — the band would silently stop printing) and
// `{{seller.*}}` tokens in their free text (which would print literally).
//
// v2 → v3: the line table had two prose columns, `details` (the route) and
// `description` (the charge text). They are one column now. A v2 layout can
// carry either or both, and leaving a stale `details` key in the array would
// render an empty cell with no header the renderer knows how to fill.
//
// v4 → v5: the `billTo` band held the customer block AND the document details,
// side by side via its own `twoColumn` switch. They are separate bands now, so
// each can take a column of the header grid. The split reproduces what the band
// printed: two-column becomes customer in its column and details in the LAST
// column of the grid (side by side); single-column becomes both in the same
// column (stacked).
//
// v3 → v4: the `header` band held the logo AND the title, positioned by three
// internal alignment slots. It is now a `logo` band and a `title` band, which
// can go in different columns. The split has to preserve the printed result, so
// the two replacements are put in a **column row** with the slot they used to
// occupy as their column: left → 1, centre → 2, right → 3. Three equal columns
// with each block in its old slot renders identically to the old three-slot
// header — the operator can then move them.
// ────────────────────────────────────────────────────────────────────────────
import { REPEATABLE_BAND_KINDS } from './bands';
import type { LabelOverrides } from './labels';
import {
  LAYOUT_SCHEMA_VERSION,
  type AddressFormat,
  type Align,
  type Band,
  type BandColumn,
  type CustomerBand,
  type DocDetailsBand,
  type DocumentLayout,
  type LineColumn,
  type LinesBand,
  type LogoBand,
  type MetaField,
  type PartyField,
  type TitleBand,
} from './types';

const LEGACY_ORGANISATION_TOKEN = /\{\{\s*seller\./g;

function retoken(text: string): string {
  return text.replace(LEGACY_ORGANISATION_TOKEN, '{{organisation.');
}

/**
 * Fold a v2 line table's `details` column into its `description` column.
 *
 * Whichever of the two was *visible* wins the width, label and alignment — that
 * is the column the operator was actually looking at. `detailsMode` carries over
 * from the old Details column when it had one; a layout that only ever used
 * `description` gets `'description'` mode, so its output does not change the
 * moment route context starts arriving.
 */
function mergeProseColumns(band: LinesBand): LinesBand {
  const columns = band.columns;
  const isProse = (c: LineColumn) =>
    c.key === 'description' || (c.key as string) === 'details';

  const details = columns.find((c) => (c.key as string) === 'details');
  const description = columns.find((c) => c.key === 'description');
  if (!details) {
    // Only ever had `description` — pin the mode so behaviour is unchanged.
    if (!description || description.detailsMode) return band;
    return {
      ...band,
      columns: columns.map((c) =>
        c.key === 'description' ? { ...c, detailsMode: 'description' as const } : c,
      ),
    };
  }

  const base = description?.visible ? description : details.visible ? details : (description ?? details);
  const merged: LineColumn = {
    ...base,
    key: 'description',
    label: base.label === 'Details' ? 'Description' : base.label,
    visible: Boolean(description?.visible || details.visible),
    detailsMode: details.detailsMode ?? base.detailsMode ?? 'description',
  };

  // Keep the earliest of the two positions; drop the other entirely.
  const next: LineColumn[] = [];
  let placed = false;
  for (const col of columns) {
    if (!isProse(col)) {
      next.push(col);
      continue;
    }
    if (!placed) {
      next.push(merged);
      placed = true;
    }
  }
  return { ...band, columns: next };
}

/** The v3 header's shape, as far as this migration needs it. */
interface LegacyHeaderBand {
  id: string;
  kind: string;
  enabled: boolean;
  spaceAfterPt: number;
  logo?: { visible?: boolean; align?: Align; widthPt?: number };
  title?: { text?: string; align?: Align; sizePt?: number; uppercase?: boolean };
  showNumber?: boolean;
  numberLabel?: string;
  showRule?: boolean;
}

/** The alignment slot a block occupied becomes the column it now sits in. */
function columnForAlign(align: Align | undefined): BandColumn {
  return align === 'center' ? 2 : align === 'right' ? 3 : 1;
}

/**
 * Split a v3 `header` into a `logo` band and a `title` band, both in a column
 * row so the printed layout does not move.
 */
function splitHeader(header: LegacyHeaderBand): Band[] {
  const logo: LogoBand = {
    id: `${header.id}-logo`,
    kind: 'logo',
    // The old band had its own enabled flag AND a logo.visible flag; the band
    // only printed a logo when both were true, so both have to agree here.
    enabled: header.enabled && (header.logo?.visible ?? false),
    spaceAfterPt: 0,
    column: columnForAlign(header.logo?.align),
    align: header.logo?.align ?? 'left',
    widthPt: header.logo?.widthPt ?? 120,
  };

  const title: TitleBand = {
    id: `${header.id}-title`,
    kind: 'title',
    enabled: header.enabled,
    // The gap after the old header now belongs to the row, which takes the
    // largest gap of its bands — so putting it here keeps the spacing.
    spaceAfterPt: header.spaceAfterPt,
    column: columnForAlign(header.title?.align),
    text: header.title?.text ?? '{{doc.typeName}}',
    align: header.title?.align ?? 'right',
    sizePt: header.title?.sizePt ?? 18,
    uppercase: header.title?.uppercase ?? true,
    showNumber: header.showNumber ?? true,
    numberLabel: header.numberLabel ?? '',
    showRule: header.showRule ?? false,
  };

  return [logo, title];
}

/** The v4 bill-to band's shape, as far as this migration needs it. */
interface LegacyBillToBand {
  id: string;
  kind: string;
  enabled: boolean;
  spaceAfterPt: number;
  column?: BandColumn;
  heading?: string;
  fields?: PartyField[];
  metaHeading?: string;
  metaFields?: MetaField[];
  metaLabels?: LabelOverrides<MetaField>;
  twoColumn?: boolean;
  addressFormat?: AddressFormat;
  addressWrapChars?: number;
}

/**
 * Split a v4 `billTo` into `customer` + `docDetails`.
 *
 * `lastColumn` is how many columns the header grid has, so a band that used to
 * print its details on the right still does.
 */
function splitBillTo(band: LegacyBillToBand, lastColumn: BandColumn): Band[] {
  const ownColumn = band.column ?? 1;
  const customer: CustomerBand = {
    id: `${band.id}-customer`,
    kind: 'customer',
    enabled: band.enabled,
    spaceAfterPt: band.spaceAfterPt,
    column: ownColumn,
    heading: band.heading ?? 'Bill to',
    fields: band.fields ?? ['name', 'code', 'addressLines'],
    addressFormat: band.addressFormat,
    addressWrapChars: band.addressWrapChars,
  };

  const details: DocDetailsBand = {
    id: `${band.id}-details`,
    kind: 'docDetails',
    enabled: band.enabled,
    spaceAfterPt: band.spaceAfterPt,
    // Side by side becomes "the far column"; stacked stays where it was.
    column: band.twoColumn ? lastColumn : ownColumn,
    heading: band.metaHeading ?? '',
    fields: band.metaFields ?? ['issueDate', 'dueDate', 'poNumber'],
    labels: band.metaLabels,
    // The old band right-aligned these rows only in its two-column mode.
    align: band.twoColumn ? 'right' : 'left',
  };

  return [customer, details];
}

/**
 * Drop duplicate bands and duplicate line columns, keeping the first of each.
 *
 * This runs on EVERY read, not only on a version bump, because a layout that
 * already carries duplicates is past the point where a migration would fix it —
 * its `schemaVersion` is current, so nothing else would ever look at it again.
 *
 * Duplicates are unrenderable rather than merely untidy. Both the band list and
 * the column list are keyed by identity (`band.id`, `col.key`), so React
 * collapses the repeat into ONE row while the PDF renderer draws both: the
 * operator sees a single "Organisation details" band printing twice, and moving
 * it up or down appears to affect the wrong one. `patchColumn` matches by key
 * too, so editing one of a duplicated pair silently edits both.
 *
 * Bands are deduplicated by id, and additionally by kind for every kind that is
 * not repeatable — two text blocks are a feature, two letterheads are a fault
 * whatever their ids say.
 */
export function dedupeLayout(
  layout: DocumentLayout,
  { warn = false }: { warn?: boolean } = {},
): DocumentLayout {
  const seenIds = new Set<string>();
  const seenSingletonKinds = new Set<string>();
  const bands: Band[] = [];
  const dropped: string[] = [];
  for (const band of layout.bands) {
    const repeatable = REPEATABLE_BAND_KINDS.includes(band.kind);
    if (seenIds.has(band.id)) {
      dropped.push(`band ${band.kind} (repeated id ${band.id})`);
      continue;
    }
    if (!repeatable && seenSingletonKinds.has(band.kind)) {
      dropped.push(`band ${band.kind} (a second one, id ${band.id})`);
      continue;
    }
    seenIds.add(band.id);
    if (!repeatable) seenSingletonKinds.add(band.kind);
    bands.push(band.kind === 'lines' ? dedupeColumns(band, dropped) : band);
  }

  if (dropped.length === 0) return layout;
  // Say what was repaired rather than repairing silently — a duplicate is a
  // symptom, and a quiet fix is what would make its cause impossible to find.
  // Only on the read path though: the renderer runs this on every frame, and a
  // warning per frame is noise, not a signal.
  if (warn) {
    console.warn(`[document layout] dropped duplicates: ${dropped.join('; ')}`);
  }
  return { ...layout, bands };
}

function dedupeColumns(band: LinesBand, dropped: string[]): LinesBand {
  const seen = new Set<string>();
  const columns = band.columns.filter((c) => {
    if (seen.has(c.key)) {
      dropped.push(`line column ${c.key} (labelled "${c.label}")`);
      return false;
    }
    seen.add(c.key);
    return true;
  });
  return columns.length === band.columns.length ? band : { ...band, columns };
}

/** Bring a layout of any earlier version up to `LAYOUT_SCHEMA_VERSION`. */
export function migrateLayout(layout: DocumentLayout, fromVersion: number): DocumentLayout {
  if (fromVersion >= LAYOUT_SCHEMA_VERSION) return dedupeLayout(layout);

  // The far column of the header grid, for placing details that used to sit on
  // the right of their own band.
  let lastColumn: BandColumn = layout.headerColumns ?? 1;
  for (const band of layout.bands) {
    if ((band.column ?? 1) > lastColumn) lastColumn = band.column ?? 1;
  }

  // flatMap because one band can become two.
  const migrated: DocumentLayout = {
    ...layout,
    bands: layout.bands.flatMap((band): Band[] => {
      if ((band.kind as string) === 'billTo') {
        return splitBillTo(band as unknown as LegacyBillToBand, lastColumn);
      }
      if ((band.kind as string) === 'header') {
        return splitHeader(band as unknown as LegacyHeaderBand);
      }
      if (band.kind === 'lines') return [mergeProseColumns(band)];
      // Compared as a string: 'seller' is no longer part of the BandKind union.
      if ((band.kind as string) === 'seller') {
        return [{ ...band, kind: 'organisation' } as Band];
      }
      if (band.kind === 'title') {
        return [{ ...band, text: retoken(band.text) }];
      }
      if (band.kind === 'text' || band.kind === 'footer') {
        return [{ ...band, body: retoken(band.body) }];
      }
      return [band];
    }),
  };

  return dedupeLayout(migrated);
}
